import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { progressPercent } from '../../../../components/progressPercent';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { approvalOf } from '../../../human-resources/attrition/approvalProgress';
import {
  ATTRITION_REQUEST_AUDIT_ENTITY_TYPES,
  type AttritionRequestListItem,
} from '../../../human-resources/attrition/attritionTypes';
import { myAttritionRequestsApi } from '../myAttritionRequestsApi';

/**
 * Self Service › Human Resources › Attrition — the attrition requests HR filed FOR the reader.
 *
 * **No Create**: HR files a request; the employee's part is the documents it asks of them,
 * uploaded from the request's own page. **No employee column** — every row is the reader's own,
 * so a name would print the same value on every line, and resolving it would need a Personnel key
 * an employee does not hold.
 */
export function MyAttritionRequestsIndexPage() {
  const navigate = useNavigate();

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      myAttritionRequestsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          category: query.facets.category?.join(','),
        },
        signal,
      ),
    [],
  );

  return (
    <IndexPage<AttritionRequestListItem>
      module="my-attrition-requests"
      breadcrumb={['Self Service', 'Human Resources', 'Attrition']}
      columns={COLUMNS}
      facets={[
        { key: 'status', label: 'Status' },
        { key: 'stage', label: 'Stage' },
        { key: 'category', label: 'Category' },
      ]}
      // What an employee opens this with is how far their papers have got.
      folderNavFacet="stage"
      auditEntityTypes={ATTRITION_REQUEST_AUDIT_ENTITY_TYPES}
      rowKey={(row) => row.id}
      fetch={fetchRows}
      searchPlaceholder="Search by request number or category"
      emptyMessage="No attrition request has been filed for you. When HR files one, the documents it needs from you appear here."
      onRowOpen={(row) => navigate(`/self-service/attrition-requests/${row.id}`)}
      openPermission="my-attrition-requests.read"
      rowProgress={(row) => {
        const progress = approvalOf(row);

        return progress ? { value: progress.done, total: progress.total, label: 'documents approved and units cleared' } : null;
      }}
      otherInfo={(row) => row}
    />
  );
}

const COLUMNS: ColumnDef<AttritionRequestListItem>[] = [
  {
    key: 'requestNumber',
    header: 'Request',
    width: 170,
    sortable: true,
    render: (row) => <TextCell value={row.requestNumber} mono />,
  },
  {
    key: 'category',
    header: 'Category',
    width: 180,
    summary: true,
    render: (row) => <TextCell value={row.categoryName} />,
  },
  {
    key: 'effectiveDate',
    header: 'Effective',
    width: 140,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={row.effectiveDate != null ? formatDateOnly(row.effectiveDate) : null} mono />,
  },
  {
    key: 'progress',
    header: 'Progress',
    width: 110,
    align: 'right',
    render: (row) => {
      const progress = approvalOf(row);

      return <TextCell value={progress ? `${progressPercent(progress.done, progress.total)}%` : '—'} mono numeric />;
    },
  },
  {
    key: 'stage',
    header: 'Stage',
    width: 170,
    sortable: true,
    stage: true,
    render: (row) => <StatusText tone="inactive">{row.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
